// ChunkLoadError detection and auto-reload helpers.
//
// When BR OS is redeployed, hashed JS chunk filenames change. Browsers that
// already loaded the previous index.html will try to lazy-load chunks that no
// longer exist on the server, causing a ChunkLoadError. This module detects
// those errors and triggers a one-shot page reload to pick up the new build.
//
// The sessionStorage guard prevents infinite reload loops if the server itself
// is broken (e.g. the new chunks 404 too).

const RELOAD_KEY = "br_chunk_reload_attempted";
const RELOAD_COOLDOWN_MS = 10_000; // 10 seconds

export function isChunkLoadError(error) {
  if (!error) return false;
  if (error.name === "ChunkLoadError") return true;
  const msg = String(error.message || error || "");
  if (/Loading chunk [\d]+ failed/i.test(msg)) return true;
  if (/Loading CSS chunk [\d]+ failed/i.test(msg)) return true;
  if (msg.includes("Failed to fetch dynamically imported module")) return true;
  return false;
}

// Returns true if a reload was triggered, false if a recent attempt already
// happened (caller should fall back to a manual error screen).
export function attemptChunkReload() {
  let stored = null;
  try {
    stored = sessionStorage.getItem(RELOAD_KEY);
  } catch {
    // sessionStorage may be unavailable (private mode, etc.) — reload anyway.
    window.location.reload();
    return true;
  }

  const now = Date.now();
  if (stored) {
    const ts = parseInt(stored, 10);
    if (!isNaN(ts) && now - ts < RELOAD_COOLDOWN_MS) {
      // Recent attempt — bail out to break the loop.
      return false;
    }
    // Stale attempt (different session, page sat idle, etc.) — clear and retry.
    try {
      sessionStorage.removeItem(RELOAD_KEY);
    } catch {}
  }

  try {
    sessionStorage.setItem(RELOAD_KEY, String(now));
  } catch {}
  window.location.reload();
  return true;
}

export function clearChunkReloadGuard() {
  try {
    sessionStorage.removeItem(RELOAD_KEY);
  } catch {}
}

// Install a window-level handler that catches lazy-import failures thrown
// outside any React render (e.g. inside event handlers or effects). Idempotent.
let _installed = false;
export function installGlobalChunkReloadHandler() {
  if (_installed || typeof window === "undefined") return;
  _installed = true;
  window.addEventListener("unhandledrejection", (event) => {
    const reason = event?.reason;
    if (isChunkLoadError(reason)) {
      attemptChunkReload();
    }
  });
  window.addEventListener("error", (event) => {
    if (isChunkLoadError(event?.error)) {
      attemptChunkReload();
    }
  });
}
